import { useMutation } from '@tanstack/react-query'
import { Button, Flex, Grid, Text } from '@radix-ui/themes'
import { formatM3, type CaptureDetail } from '~/features/index-captures/alignment'
import { useTRPC } from '~/utils/trpc'

interface DecisionFormProps {
  detail: CaptureDetail
  onDecided: () => Promise<void>
}

export const DecisionForm = ({ detail, onDecided }: DecisionFormProps) => {
  const trpc = useTRPC()
  const decide = useMutation(trpc.ops.indexCaptures.decide.mutationOptions({ onSuccess: onDecided }))

  const hasReading = detail.readingValue !== null && detail.capturedAt !== null && detail.stationId !== null
  const pending = decide.isPending ? decide.variables.decision : null

  return (
    <Flex direction="column" gap="3">
      <Text size="2" color="gray">
        {hasReading
          ? `Either way, ${formatM3(detail.readingValue)} becomes the station's index reading and the next check measures from it. Only "aligned" counts toward the installation's automatic index alignment.`
          : 'There is no reading on this capture, so it can only be closed as not aligned. Enter the index above first to record one.'}
      </Text>

      {decide.error ? (
        <Text size="2" color="red">
          {decide.error.message}
        </Text>
      ) : null}

      <Grid columns="2" gap="3">
        <Button
          color="green"
          disabled={!hasReading || decide.isPending}
          loading={pending === 'aligned'}
          onClick={() => decide.mutate({ captureId: detail.id, decision: 'aligned' })}
        >
          Mark as aligned
        </Button>
        <Button
          color="red"
          variant="soft"
          disabled={decide.isPending}
          loading={pending === 'not_aligned'}
          onClick={() => decide.mutate({ captureId: detail.id, decision: 'not_aligned' })}
        >
          Mark as not aligned
        </Button>
      </Grid>
    </Flex>
  )
}
